<script setup lang="ts">
import type { PaymentsEmbed } from '@/Pejer/tables/Payments'
import { Building2, CreditCard, FileText, Landmark } from 'lucide-vue-next'
import { Badge } from 'pejer/src/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'pejer/src/components/ui/tooltip'
import { computed } from 'vue'

const props = defineProps<{
  payment: {
    fundsLocation?: PaymentsEmbed.FundsLocation
    paymentProvider: string
    kidNorwayInvoice?: { receiverIsDirectPaymentAccount?: boolean } | null
  }
  hideMethod?: boolean
}>()

const fundsLocations = {
  ClubAccount: {
    label: 'Klubbens konto',
    icon: Landmark,
    description:
      'Betales direkte til klubbens egen bankkonto. Refusjon må gjøres fra klubbens nettbank.',
  },
  BuckyClientAccount: {
    label: 'PayBucky klientkonto',
    icon: Building2,
    description: 'Betales til PayBuckys klientkonto og utbetales til klubben.',
  },
  Stripe: {
    label: 'Stripe',
    icon: CreditCard,
    description: 'Kortbetaling via Stripe.',
  },
  Dintero: {
    label: 'Kravia (Dintero)',
    icon: FileText,
    description:
      'Betales til Kravias konto. Dintero fordeler og utbetaler. Betalte fakturaer kan ikke refunderes.',
  },
} as const

const location = computed(() => {
  const key =
    props.payment.fundsLocation ??
    (props.payment.kidNorwayInvoice?.receiverIsDirectPaymentAccount ? 'ClubAccount' : undefined)
  return key && key in fundsLocations ? fundsLocations[key as keyof typeof fundsLocations] : null
})

const paymentMethods: Record<string, string> = {
  KidNorway: 'KID-faktura',
  bank2bank: 'bankoverføring',
  SwedbankPay: 'Swedbank Pay',
  Easy: 'Nets Easy',
  Bambora: 'Bambora',
}

const paymentMethod = computed(() =>
  !props.hideMethod && props.payment.fundsLocation === 'BuckyClientAccount'
    ? paymentMethods[props.payment.paymentProvider]
    : undefined
)
</script>
<template>
  <TooltipProvider v-if="location">
    <Tooltip>
      <TooltipTrigger as-child>
        <Badge variant="secondary" class="text-xs gap-1 w-fit">
          <component :is="location.icon" :size="12" />
          <span class="flex flex-col">
            {{ location.label }}
            <span v-if="paymentMethod" class="font-normal text-muted-foreground">
              via {{ paymentMethod }}
            </span>
          </span>
        </Badge>
      </TooltipTrigger>
      <TooltipContent class="max-w-64">{{ location.description }}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
</template>
